import axios, { InternalAxiosRequestConfig } from 'axios';
import { AccessToken, IDToken } from '@okta/okta-auth-js';
/* eslint-disable no-param-reassign */
import { sessionExpired } from 'store/auth/auth.action';
import { queryClient } from 'utils/initializeQueryClient';
import { analytics } from '@redislabsdev/cloud-ui-analytics';
import {
  genericErrorStatus,
  serviceUnavailableStatus,
  unauthorizedStatus
} from '../../../utils/constants/api/statuses';
import { extractCbError } from '../../../utils/helpers/extractCbError';
import logHttpResponseError from '../../sentry/utils/logHttpResponseError';
import { ExtendedAxiosError } from '../../../types/interfaces';
import history from '../../../hashHistory';
import { routes } from '../../../utils/constants/routes';
import { extractErrorAndShowToast } from '../../../utils/helpers/extractErrorAndShowToast';
import { SmOktaAuth } from '../../../utils/oktaInstance';
import { USER_MFA_REQUIRED } from '../../../utils/constants/api/codes';
import store from '../../../store';
import shouldTriggerCaptcha from './utils/shouldTriggerCaptcha';
import captchaHandler from './utils/captchaHandler';

const setRequestInterceptor =
  (accessToken: AccessToken | null) => (requestConfig: InternalAxiosRequestConfig) => {
    const isLoginRequest = requestConfig.url?.includes('/login');

    if (isLoginRequest && accessToken) {
      requestConfig.headers.Authorization = `Bearer ${accessToken.accessToken}`;
    }

    return requestConfig;
  };

const setRequestInterceptorForIdToken =
  (idToken: IDToken | null) => (requestConfig: InternalAxiosRequestConfig) => {
    const isLoginRequest = requestConfig.url?.includes('/login');

    if (isLoginRequest && idToken) {
      requestConfig.headers['SM-Id-Token'] = `${idToken.idToken}`;
    }

    return requestConfig;
  };

const interceptorId = { id: null, tokenId: null };

export const setOktaAuthorizationHeader = (accessToken: AccessToken) => {
  if (interceptorId.id !== null) {
    RedisApiAxiosInstance.interceptors.request.eject(interceptorId.id);
  }

  interceptorId.id = RedisApiAxiosInstance.interceptors.request.use(
    setRequestInterceptor(accessToken)
  );
};

export const setOktaIdTokenHeader = (idToken: IDToken) => {
  if (interceptorId.tokenId !== null) {
    RedisApiAxiosInstance.interceptors.request.eject(interceptorId.tokenId);
  }

  interceptorId.tokenId = RedisApiAxiosInstance.interceptors.request.use(
    setRequestInterceptorForIdToken(idToken)
  );
};

// Endpoints that trigger logout on 500 errors (in addition to 401)
const redirectOnGenericErrorList = ['users', 'accounts'];

// Microservice endpoints that handle their own authentication errors
const MICROSERVICE_SEGMENTS = [
  'kyc',
  'maintenance-window',
  'cloud-transit-gateway',
  'rdi',
  'tagging',
  'cloud-db-service',
  'cloud-db-endpoints-service'
];

const shouldRedirectOnGenericError = (url = '', whitelist = []) => {
  return whitelist.some((segment) => url.includes(segment));
};

const RedisApiAxiosInstance = axios.create({
  withCredentials: true
});

export const setCSRFHeader = (token: string) => {
  RedisApiAxiosInstance.defaults.headers.common['X-CSRF-Token'] = token;
};

RedisApiAxiosInstance.interceptors.response.use(
  async (response) => {
    if (shouldTriggerCaptcha(response)) {
      await captchaHandler();

      return RedisApiAxiosInstance(response.config);
    }

    return response;
  },
  async (error: ExtendedAxiosError) => {
    if (error?.response) {
      const {
        status,
        data,
        config: { url }
      } = error.response;

      error.response.errorInfo = extractCbError(data);

      logHttpResponseError(error);

      const isGenericErrWithPermission =
        status === genericErrorStatus &&
        shouldRedirectOnGenericError(url, redirectOnGenericErrorList);

      if (status === unauthorizedStatus || isGenericErrWithPermission) {
        if (isGenericErrWithPermission) {
          extractErrorAndShowToast(error);
        }

        // Pass through MFA errors - component will handle MFA flow
        if (data?.errors?.code === USER_MFA_REQUIRED) {
          return Promise.reject(error);
        }

        // Pass through microservice endpoint errors - microservices handle their own auth
        const isMicroserviceEndpoint = !url
          ? false
          : MICROSERVICE_SEGMENTS.some((segment) => url.includes(segment));

        if (isMicroserviceEndpoint) {
          return Promise.reject(error);
        }

        // Save current location for post-login redirect
        const { search, pathname } = history.location;
        const comingFromLogout = window.sessionStorage.getItem('logOutFlag');
        const isLoginPath = pathname.includes(routes.login);

        if (!isLoginPath && !comingFromLogout) {
          window.sessionStorage.setItem('locationPathName', pathname);

          if (window.location.pathname.length > 1) {
            window.sessionStorage.setItem('locationEnvPath', window.location.pathname);
          }
        }

        if (search) {
          window.sessionStorage.setItem('queryParams', search);
        }

        window.sessionStorage.removeItem('logOutFlag');

        // Clear Okta session and tokens
        try {
          const oktaAuth = SmOktaAuth.getInstance();
          setOktaAuthorizationHeader(null);
          await oktaAuth?.tokenManager.clear();
        } catch (err) {
          // eslint-disable-next-line no-console
          console.log('err: ', { ...err });
        } finally {
          store.dispatch(sessionExpired());
          analytics.reset();
        }

        return Promise.reject(error);
      }

      if (status === serviceUnavailableStatus && history.location.pathname !== routes.maintenance) {
        queryClient.removeQueries();
        history.push(routes.maintenance);
      }
    }

    return Promise.reject(error);
  }
);

export default RedisApiAxiosInstance;
